import React, { useState } from "react";
import { useApp } from "../state/AppContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

const GRADES = ["PreK", "Kindergarten", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "No child / Community member"];

export default function WelcomeScreen() {
  const { createUser, setScreen, t, pathParam } = useApp();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", grade: "", consent: false, media_consent: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validStep = () => {
    if (step === 0) return form.first_name.trim() && form.last_name.trim();
    if (step === 1) return /\S+@\S+\.\S+/.test(form.email);
    if (step === 2) return !!form.grade;
    return false;
  };

  const next = async () => {
    if (!validStep()) { toast.error(t("toast_form_error")); return; }
    if (step < 2) { setStep(step + 1); return; }
    try {
      setSubmitting(true);
      await createUser({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        grade: form.grade,
        consent: !!form.consent,
        media_consent: !!form.media_consent,
      });
      toast.success(t("toast_welcome"));
      if (pathParam === "kindness") setScreen("gallery");
      else if (pathParam === "spinner") setScreen("activities");
      else setScreen("home");
    } catch {
      toast.error(t("toast_signup_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-scl-cream" data-testid="welcome-screen">
      <div className="h-3 w-full bg-scl-coral" />
      <div className="flex-1 flex flex-col px-6 pt-8 pb-32">
        <div className="flex items-center justify-between mb-6">
          <img
            src="https://customer-assets-0z36b82j.emergentagent.net/job_scl-kindness/artifacts/6382qm3g_SCL-final-logo-clearspace-namelogo-cropped%20%282%29.png"
            alt="School for Community Learning"
            className="h-14 w-auto object-contain"
            data-testid="scl-wordmark"
          />
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-scl-teal font-semibold">{t("scl_indianapolis")}</div>
            <div className="text-[11px] text-scl-charcoal/60 font-medium">{t("scl_challenge_label")}</div>
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden mb-6 shadow-warm-sm h-40">
          <img
            src="https://images.unsplash.com/photo-1533222535026-754c501569dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwaGFwcHklMjBraWRzJTIwc2Nob29sJTIwY29tbXVuaXR5fGVufDB8fHx8MTc4NDE1NzA0Nnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-scl-charcoal/60 via-scl-charcoal/10 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-90">{t("welcome_hero_kicker")}</div>
            <div className="font-serif text-lg font-bold leading-tight">{t("welcome_hero_tag")}</div>
          </div>
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] text-scl-charcoal font-bold mb-4" data-testid="welcome-headline">
          {t("welcome_hero_title")}
        </h1>
        <p className="text-base text-scl-charcoal/80 leading-relaxed max-w-md mb-8">{t("welcome_hero_lead")}</p>

        <div className="flex items-center gap-2 mb-6" data-testid="welcome-progress">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-scl-coral" : i < step ? "w-6 bg-scl-teal" : "w-2 bg-scl-border"}`} />
          ))}
          <div className="ml-3 text-xs uppercase tracking-widest text-scl-charcoal/60 font-medium">{t("step_of", { n: step + 1 })}</div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-warm-sm animate-fade-up" key={step}>
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-xs uppercase tracking-widest text-scl-charcoal/60 font-medium">{t("first_name")}</Label>
                <Input data-testid="input-first-name" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="mt-2 h-12 rounded-xl border-scl-border bg-white text-base" placeholder="Ada" />
              </div>
              <div>
                <Label className="font-sans text-xs uppercase tracking-widest text-scl-charcoal/60 font-medium">{t("last_name")}</Label>
                <Input data-testid="input-last-name" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="mt-2 h-12 rounded-xl border-scl-border bg-white text-base" placeholder="Lovelace" />
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-xs uppercase tracking-widest text-scl-charcoal/60 font-medium">{t("email")} <span className="text-scl-brick normal-case tracking-normal">*</span></Label>
                <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12 rounded-xl border-scl-border bg-white text-base" placeholder="you@example.com" />
                <p className="mt-1.5 text-[11px] text-scl-charcoal/50">{t("email_help")}</p>
              </div>
              <div>
                <Label className="font-sans text-xs uppercase tracking-widest text-scl-charcoal/60 font-medium">{t("phone_number")} <span className="text-scl-charcoal/40 normal-case tracking-normal">— {t("optional")}</span></Label>
                <Input data-testid="input-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-12 rounded-xl border-scl-border bg-white text-base" placeholder="(317) 555-0123" />
                <p className="mt-1.5 text-[11px] text-scl-charcoal/50">{t("phone_help")}</p>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <Label className="font-sans text-xs uppercase tracking-widest text-scl-charcoal/60 font-medium">{t("grade_label")}</Label>
                <Select value={form.grade} onValueChange={(v) => update("grade", v)}>
                  <SelectTrigger data-testid="select-grade" className="mt-2 h-12 rounded-xl border-scl-border bg-white text-base">
                    <SelectValue placeholder={t("grade_placeholder")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {GRADES.map((g) => <SelectItem key={g} value={g} data-testid={`grade-option-${g}`}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-start gap-3 cursor-pointer group min-h-[44px]" data-testid="consent-label">
                <Checkbox checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} data-testid="consent-checkbox" className="mt-1 border-scl-teal data-[state=checked]:bg-scl-teal data-[state=checked]:border-scl-teal" />
                <span className="text-sm text-scl-charcoal/80 leading-relaxed">{t("consent_contact")}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group min-h-[44px]" data-testid="media-consent-label">
                <Checkbox checked={form.media_consent} onCheckedChange={(v) => update("media_consent", !!v)} data-testid="media-consent-checkbox" className="mt-1 border-scl-teal data-[state=checked]:bg-scl-teal data-[state=checked]:border-scl-teal" />
                <span className="text-sm text-scl-charcoal/80 leading-relaxed">{t("consent_media")}</span>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-[480px] mx-auto px-6 pb-6 pt-3 bg-gradient-to-t from-scl-cream via-scl-cream/95 to-transparent">
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                data-testid="back-btn"
                onClick={() => setStep(step - 1)}
                className="h-14 px-5 rounded-full border border-scl-border bg-white text-scl-charcoal font-medium flex items-center gap-2 hover:bg-scl-cream transition-all active:scale-95 min-h-[44px]"
              >
                <ArrowLeft className="w-4 h-4" /> {t("back")}
              </button>
            )}
            <button
              data-testid="next-btn"
              disabled={submitting}
              onClick={next}
              className="flex-1 h-14 rounded-full bg-scl-coral text-white font-semibold text-lg flex items-center justify-center gap-2 shadow-warm hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-70 min-h-[44px]"
            >
              {step < 2 ? t("continue") : submitting ? t("cta_signing_up") : t("cta_lets_go")}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
